/**
 * Theme Selector Custom Element
 * Handles theme card selection and modal for theme details
 */
class ThemeSelector extends HTMLElement {
  constructor() {
    super();

    this.themes = [];
    this.selectedThemes = new Set();
    this.currentModalTheme = null;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;

    // Initialize lightbox state
    this.currentGalleryImages = [];
    this.currentImageIndex = 0;

    // Parse theme data from data attributes
    this.parseThemeData();

    // Render the component
    this.render();

    // Set up event listeners
    this.setupEventListeners();

    // Auto-preselect default theme after a short delay to ensure all listeners are ready
    setTimeout(() => {
      this.preselectDefaultTheme();
    }, 50);
  }

  // Use global translations object
  get strings() {
    return theme.personalizedBookStrings || {};
  }

  parseThemeData() {
    try {
      const themesData = this.dataset.themes;
      if (themesData) {
        this.themes = JSON.parse(themesData);
      }

      const modalData = this.dataset.modalData;
      if (modalData) {
        this.themeModalData = JSON.parse(modalData);
      }
    } catch (error) {
      console.error("Error parsing theme data:", error);
      this.themes = [];
      this.themeModalData = {};
    }
  }

  render() {
    const s = this.strings;
    this.innerHTML = `
      <!-- Theme Cards Grid -->
      <div class="pbk-grid" data-theme-grid>
        ${this.themes.map((theme) => this.renderThemeCard(theme)).join("")}
      </div>

      <!-- Theme Details Modal -->
      <div class="pbk-theme-modal" data-modal>
        <div class="pbk-theme-modal-content">
          <button class="pbk-theme-modal-close" type="button" aria-label="${s.close || "Close"}" data-modal-close>×</button>
          <div class="pbk-theme-modal-header">
            <h2 class="pbk-theme-modal-title" data-modal-title>${s.themeTitle || "Theme title"}</h2>
            <p class="pbk-theme-modal-subtitle" data-modal-subtitle>${s.themeSubtitle || "Theme subtitle"}</p>
          </div>
          <div class="pbk-theme-modal-body">
            <!-- Image Gallery -->
            <div class="pbk-theme-gallery" data-modal-gallery>
              <!-- Gallery images will be populated dynamically -->
            </div>
            
            <!-- Description -->
            <div class="pbk-theme-description" data-modal-description>
              <!-- Description will be populated dynamically -->
            </div>
            
            <!-- Features -->
            <div class="pbk-theme-features" data-modal-features>
              <!-- Features will be populated dynamically -->
            </div>
          </div>
          <div class="pbk-theme-modal-footer">
            <button type="button" class="pbk-theme-select-btn" data-modal-select>
              ${s.selectThisTheme || "Select this theme"}
            </button>
          </div>
        </div>
      </div>

      <!-- Image Lightbox -->
      <div class="pbk-lightbox" data-lightbox>
        <button class="pbk-lightbox-close" type="button" aria-label="${s.closePreview || "Close preview"}" data-lightbox-close>×</button>
        <button class="pbk-lightbox-prev" type="button" aria-label="${s.previousImage || "Previous image"}" data-lightbox-prev>
          <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>
        <button class="pbk-lightbox-next" type="button" aria-label="${s.nextImage || "Next image"}" data-lightbox-next>
          <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
          </svg>
        </button>
        <div class="pbk-lightbox-content">
          <img class="pbk-lightbox-image" data-lightbox-image src="" alt="">
          <div class="pbk-lightbox-counter" data-lightbox-counter></div>
        </div>
      </div>
    `;
  }

  renderThemeCard(theme) {
    const s = this.strings;
    const bookTitle = this.normalizeBookTitle(theme.bookTitle);
    const bookTitleHtml = this.formatTextWithLineBreaks(bookTitle);
    return `
      <div class="pbk-theme-card" 
           data-theme-key="${this.escapeHtml(theme.key)}" 
           data-theme-title="${this.escapeHtml(theme.title)}" 
           data-theme-book-title="${this.escapeHtml(bookTitle).replace(/\n/g, "&#10;")}"
           data-theme-block-id="${this.escapeHtml(theme.blockId)}"
           data-theme-product-handle="${this.escapeHtml(theme.productHandle || "")}"
           data-theme-variants="${this.escapeHtml(JSON.stringify(theme.variants || []))}">
        <div class="pbk-theme-selected-badge" aria-hidden="true">${s.selected || "Selected"}</div>
        ${
          theme.image
            ? `<img src="${this.escapeHtml(
                theme.image,
              )}" loading="lazy" alt="${this.escapeHtml(theme.title)}">`
            : ""
        }
        <div class="pbk-theme-meta">
          <div class="pbk-theme-title">${this.escapeHtml(theme.title)}</div>
          ${
            bookTitle
              ? `<div class="pbk-theme-book-title">${bookTitleHtml}</div>`
              : ""
          }
          <div class="pbk-caption">${this.escapeHtml(theme.caption || "")}</div>
        </div>
        <div class="pbk-theme-actions">
          <a type="button" class="pbk-theme-view-more-btn" data-theme-block-id="${this.escapeHtml(
            theme.blockId,
          )}" aria-label="${s.viewDetails || "View details"} ${this.escapeHtml(
            theme.title,
          )}">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.71.71l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z"></path></svg>
            <span>${s.viewDetails || "View details"}</span>
          </a>
        </div>

        <!-- Explicit theme selection control -->
        <div class="pbk-theme-select">
          <input type="checkbox" 
                 class="pbk-theme-select-checkbox" 
                 id="pbk-theme-select-${this.escapeHtml(theme.blockId)}" 
                 aria-label="${s.select || "Select"} ${this.escapeHtml(
                   theme.title,
                 )}">
          <label for="pbk-theme-select-${this.escapeHtml(
            theme.blockId,
          )}" class="pbk-theme-select-text">${s.select || "Select"}</label>
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    // Theme card clicks
    const cards = this.querySelectorAll("[data-theme-key]");
    cards.forEach((card) => {
      const checkbox = card.querySelector(".pbk-theme-select-checkbox");

      // Card click handler
      card.addEventListener("click", (e) => {
        // Ignore clicks on inputs/labels, but allow view more button
        if (
          e.target.closest("input,select,textarea,label") &&
          !e.target.closest(".pbk-theme-view-more-btn")
        ) {
          return;
        }

        this.toggleThemeSelection(card, checkbox);
      });

      // Checkbox change handler
      checkbox?.addEventListener("change", (e) => {
        e.stopPropagation();
        this.toggleThemeSelection(card, checkbox);
      });

      // View more button
      const viewMoreBtn = card.querySelector(".pbk-theme-view-more-btn");
      viewMoreBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        const blockId = viewMoreBtn.dataset.themeBlockId;
        if (blockId) {
          this.openModal(blockId);
        }
      });
    });

    // Modal event listeners
    this.setupModalListeners();

    // Lightbox event listeners
    this.setupLightboxListeners();
  }

  setupModalListeners() {
    const modal = this.querySelector("[data-modal]");
    const modalClose = this.querySelector("[data-modal-close]");
    const modalSelect = this.querySelector("[data-modal-select]");

    // Close button
    modalClose?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.closeModal();
    });

    // Backdrop click
    modal?.addEventListener("click", (e) => {
      if (e.target === modal) {
        this.closeModal();
      }
    });

    // Escape key
    document.addEventListener("keydown", (e) => {
      if (
        (e.key === "Escape" || e.key === "Esc") &&
        modal?.classList.contains("is-open")
      ) {
        this.closeModal();
      }
    });

    // Select button in modal
    modalSelect?.addEventListener("click", () => {
      if (this.currentModalTheme) {
        const card = this.querySelector(
          `[data-theme-block-id="${this.currentModalTheme}"]`,
        );
        const checkbox = card?.querySelector(".pbk-theme-select-checkbox");
        if (card && checkbox) {
          this.toggleThemeSelection(card, checkbox);
        }
        this.closeModal();
      }
    });
  }

  setupLightboxListeners() {
    const lightbox = this.querySelector("[data-lightbox]");
    const lightboxClose = this.querySelector("[data-lightbox-close]");
    const lightboxPrev = this.querySelector("[data-lightbox-prev]");
    const lightboxNext = this.querySelector("[data-lightbox-next]");

    // Close button
    lightboxClose?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.closeLightbox();
    });

    // Navigation buttons
    lightboxPrev?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.showPrevImage();
    });

    lightboxNext?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.showNextImage();
    });

    // Click outside to close
    lightbox?.addEventListener("click", (e) => {
      if (e.target === lightbox) {
        this.closeLightbox();
      }
    });

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (!lightbox?.classList.contains("is-open")) return;

      if (e.key === "Escape" || e.key === "Esc") {
        this.closeLightbox();
      } else if (e.key === "ArrowLeft") {
        this.showPrevImage();
      } else if (e.key === "ArrowRight") {
        this.showNextImage();
      }
    });
  }

  toggleThemeSelection(card, checkbox) {
    const themeKey = card.dataset.themeKey;
    const blockId = card.dataset.themeBlockId;

    card.classList.toggle("is-selected");
    const nowSelected = card.classList.contains("is-selected");

    if (checkbox) {
      checkbox.checked = nowSelected;
    }

    // Update selected themes set
    if (nowSelected) {
      if (!card.dataset.groupId) {
        card.dataset.groupId = this.generateUUID();
      }
      this.selectedThemes.add(themeKey);
    } else {
      this.selectedThemes.delete(themeKey);
    }

    // Emit selection change event
    this.dispatchEvent(
      new CustomEvent("theme-selection-changed", {
        detail: {
          selectedThemes: this.getSelectedThemes(),
          count: this.selectedThemes.size,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  openModal(blockId) {
    const modal = this.querySelector("[data-modal]");
    const themeData = this.themeModalData?.[blockId];

    if (!themeData || !modal) return;

    // Update modal content
    const modalTitle = this.querySelector("[data-modal-title]");
    const modalSubtitle = this.querySelector("[data-modal-subtitle]");
    const modalGallery = this.querySelector("[data-modal-gallery]");
    const modalDescription = this.querySelector("[data-modal-description]");
    const modalFeatures = this.querySelector("[data-modal-features]");

    if (modalTitle) modalTitle.textContent = themeData.title || "";
    if (modalSubtitle) modalSubtitle.textContent = themeData.caption || "";

    // Update gallery
    if (modalGallery) {
      modalGallery.innerHTML = "";
      if (themeData.gallery && themeData.gallery.length > 0) {
        // Store gallery images for lightbox
        this.currentGalleryImages = themeData.gallery.filter((url) => url);

        this.currentGalleryImages.forEach((imageUrl, index) => {
          const galleryItem = document.createElement("div");
          galleryItem.className = "pbk-theme-gallery-item";
          galleryItem.style.cursor = "pointer";
          galleryItem.innerHTML = `<img src="${this.escapeHtml(
            imageUrl,
          )}" alt="${this.escapeHtml(themeData.title)}" loading="lazy">`;

          // Add click handler to open lightbox
          galleryItem.addEventListener("click", () => {
            this.openLightbox(index);
          });

          modalGallery.appendChild(galleryItem);
        });
      }
    }

    // Update description
    if (modalDescription) {
      modalDescription.innerHTML = "";
      if (themeData.description && themeData.description.trim()) {
        const descDiv = document.createElement("div");
        descDiv.innerHTML = themeData.description;
        modalDescription.appendChild(descDiv);
      } else {
        modalDescription.innerHTML = `<p>${this.strings.noDescription || "No detailed description available for this theme."}</p>`;
      }
    }

    // Update features
    if (modalFeatures) {
      modalFeatures.innerHTML = "";
      if (themeData.features && themeData.features.trim()) {
        const featuresArray = themeData.features
          .split(",")
          .map((f) => f.trim())
          .filter((f) => f);
        if (featuresArray.length > 0) {
          const featuresList = document.createElement("div");
          featuresList.className = "pbk-theme-features-list";
          featuresArray.forEach((feature) => {
            const featureTag = document.createElement("span");
            featureTag.className = "pbk-theme-feature-tag";
            featureTag.textContent = feature;
            featuresList.appendChild(featureTag);
          });
          modalFeatures.appendChild(featuresList);
        }
      }
    }

    this.currentModalTheme = blockId;
    modal.classList.add("is-open");
    document.body.style.overflow = "hidden";
  }

  closeModal() {
    const modal = this.querySelector("[data-modal]");
    if (modal) {
      modal.classList.remove("is-open");
      document.body.style.overflow = "";
      this.currentModalTheme = null;
    }
  }

  openLightbox(imageIndex) {
    const lightbox = this.querySelector("[data-lightbox]");
    if (!lightbox || this.currentGalleryImages.length === 0) return;

    this.currentImageIndex = imageIndex;
    this.updateLightboxImage();
    lightbox.classList.add("is-open");
  }

  closeLightbox() {
    const lightbox = this.querySelector("[data-lightbox]");
    if (lightbox) {
      lightbox.classList.remove("is-open");
    }
  }

  showPrevImage() {
    if (this.currentGalleryImages.length === 0) return;

    this.currentImageIndex =
      (this.currentImageIndex - 1 + this.currentGalleryImages.length) %
      this.currentGalleryImages.length;
    this.updateLightboxImage();
  }

  showNextImage() {
    if (this.currentGalleryImages.length === 0) return;

    this.currentImageIndex =
      (this.currentImageIndex + 1) % this.currentGalleryImages.length;
    this.updateLightboxImage();
  }

  updateLightboxImage() {
    const lightboxImage = this.querySelector("[data-lightbox-image]");
    const lightboxCounter = this.querySelector("[data-lightbox-counter]");
    const lightboxPrev = this.querySelector("[data-lightbox-prev]");
    const lightboxNext = this.querySelector("[data-lightbox-next]");

    if (lightboxImage && this.currentGalleryImages.length > 0) {
      const currentImageUrl = this.currentGalleryImages[this.currentImageIndex];
      lightboxImage.src = currentImageUrl;
      const imageOfText = (
        this.strings.imageOf || "Image {{ current }} of {{ total }}"
      )
        .replace("{{ current }}", this.currentImageIndex + 1)
        .replace("{{ total }}", this.currentGalleryImages.length);
      lightboxImage.alt = imageOfText;
    }

    if (lightboxCounter) {
      lightboxCounter.textContent = `${this.currentImageIndex + 1} / ${
        this.currentGalleryImages.length
      }`;
    }

    // Show/hide navigation buttons based on number of images
    if (this.currentGalleryImages.length <= 1) {
      lightboxPrev?.classList.add("pbk-hidden");
      lightboxNext?.classList.add("pbk-hidden");
    } else {
      lightboxPrev?.classList.remove("pbk-hidden");
      lightboxNext?.classList.remove("pbk-hidden");
    }
  }

  preselectDefaultTheme() {
    // If there's only one theme, auto-select it
    if (this.themes.length === 1) {
      const themeKey = this.themes[0].key;
      const card = this.querySelector(`[data-theme-key="${themeKey}"]`);
      const checkbox = card?.querySelector(".pbk-theme-select-checkbox");
      if (card && !card.classList.contains("is-selected")) {
        this.toggleThemeSelection(card, checkbox);
      }
      return;
    }

    // Otherwise, check for default theme from settings or URL
    const defaultTheme = this.dataset.defaultTheme || "";
    const urlTheme = new URLSearchParams(location.search).get("theme") || "";
    const themeToPreselect = (urlTheme || defaultTheme).toLowerCase();

    if (themeToPreselect) {
      const card = this.querySelector(`[data-theme-key="${themeToPreselect}"]`);
      const checkbox = card?.querySelector(".pbk-theme-select-checkbox");
      if (card && !card.classList.contains("is-selected")) {
        this.toggleThemeSelection(card, checkbox);
      }
    }
  }

  // Public API
  getSelectedThemes() {
    const cards = Array.from(
      this.querySelectorAll("[data-theme-key].is-selected"),
    );
    return cards.map((card) => {
      let variants = [];
      try {
        variants = JSON.parse(card.dataset.themeVariants || "[]");
      } catch (e) {
        variants = [];
      }
      return {
        key: card.dataset.themeKey,
        title: card.dataset.themeTitle,
        bookTitle: this.normalizeBookTitle(card.dataset.themeBookTitle || ""),
        blockId: card.dataset.themeBlockId,
        groupId: card.dataset.groupId || this.generateUUID(),
        productHandle: card.dataset.themeProductHandle || "",
        variants,
      };
    });
  }

  getSelectedCount() {
    return this.selectedThemes.size;
  }

  deselectTheme(themeKey) {
    const card = this.querySelector(`[data-theme-key="${themeKey}"]`);
    const checkbox = card?.querySelector(".pbk-theme-select-checkbox");

    if (card && card.classList.contains("is-selected")) {
      card.classList.remove("is-selected");
      if (checkbox) checkbox.checked = false;
      this.selectedThemes.delete(themeKey);

      // Emit selection change event
      this.dispatchEvent(
        new CustomEvent("theme-selection-changed", {
          detail: {
            selectedThemes: this.getSelectedThemes(),
            count: this.selectedThemes.size,
          },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  // Utility methods
  generateUUID() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  escapeHtml(text) {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Convert newline characters into <br> tags, safely.
   * Accepts already-normalized plain text; escapes all HTML first, then injects <br>.
   */
  formatTextWithLineBreaks(text) {
    if (!text) return "";
    const escaped = this.escapeHtml(text);
    return escaped.replace(/\r?\n/g, "<br>");
  }

  normalizeBookTitle(text) {
    const raw = (text || "").toString();
    // Allow merchant-friendly line breaks via literal newlines or "<br>" in settings.
    const withNewlines = raw
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/\r\n/g, "\n");
    const t = withNewlines.trim();
    // In theme settings, "-" is used as an explicit "no title" placeholder.
    return t === "-" ? "" : t;
  }
}

// Register the custom element
customElements.define("theme-selector", ThemeSelector);
